<template>
  <AppLayout pageTitle="Transactions">
    <!-- Header Actions -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body py-3 px-4">
            <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h5 class="fw-bold mb-1" style="color: #0f172a;">Transaction History</h5>
                <small class="text-muted">Manage your payments and view your records</small>
              </div>
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn fw-semibold" @click="showStkModal = true"
                        style="background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; border-radius: 10px; padding: 8px 18px;">
                  <i class="ph-duotone ph-money me-1"></i> Pay via M-Pesa
                </button>
                <button class="btn btn-outline-success fw-semibold" @click="showStatementModal = true" style="border-radius: 10px; padding: 8px 18px;">
                  <i class="ph-duotone ph-file-pdf me-1"></i> Statement
                </button>
                <router-link to="/self-sort" class="btn btn-outline-primary fw-semibold" style="border-radius: 10px; padding: 8px 18px;">
                  <i class="ph-duotone ph-clipboard-text me-1"></i> Self Sort
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body py-3 px-4">
            <div class="row g-3 align-items-end">
              <div class="col-md-3 col-sm-6">
                <label class="form-label fw-medium f-s-13 text-muted">From Date</label>
                <input type="date" class="form-control" v-model="filters.startDate" @change="applyFilters" style="border-radius: 10px; padding: 10px 14px;">
              </div>
              <div class="col-md-3 col-sm-6">
                <label class="form-label fw-medium f-s-13 text-muted">To Date</label>
                <input type="date" class="form-control" v-model="filters.endDate" @change="applyFilters" style="border-radius: 10px; padding: 10px 14px;">
              </div>
              <div class="col-md-3 col-sm-6 d-flex align-items-end">
                <button class="btn btn-light w-100 fw-medium" @click="clearFilters" style="border-radius: 10px; padding: 10px 14px;">
                  <i class="ph-bold ph-x me-1"></i> Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="row">
      <div class="col-12">
        <div class="card border-0" style="border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.06);">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead>
                  <tr style="background: #f8fafc;">
                    <th class="border-0 f-s-13 fw-semibold text-muted px-4 py-3">Reference</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Amount</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Type</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Date</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Account</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Account No.</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Status</th>
                    <th class="border-0 f-s-13 fw-semibold text-muted py-3">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLoading">
                    <td colspan="8" class="text-center py-5">
                      <div class="spinner-border text-primary spinner-border-sm"></div>
                      <p class="text-muted mb-0 mt-2 f-s-13">Loading transactions...</p>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedTransactions.length === 0">
                    <td colspan="8" class="text-center py-5">
                      <i class="ph-duotone ph-receipt f-s-30 text-muted"></i>
                      <p class="text-muted mb-0 mt-2">No transactions found</p>
                    </td>
                  </tr>
                  <tr v-for="transaction in paginatedTransactions" :key="transaction.id">
                    <td class="px-4 py-3">
                      <span class="fw-semibold f-s-13">{{ transaction.transaction_reference || transaction.id }}</span>
                    </td>
                    <td class="py-3">
                      <span class="fw-bold f-s-14" :class="store.balancesHidden ? 'balance-blurred' : 'balance-visible'" style="color: #10b981;">KES {{ Number(transaction.amount).toLocaleString() }}</span>
                    </td>
                    <td class="py-3"><span class="f-s-13 text-muted">{{ transaction.transaction_type }}</span></td>
                    <td class="py-3"><span class="f-s-13 text-muted">{{ formatDate(transaction.created_at) }}</span></td>
                    <td class="py-3">
                      <span class="badge rounded-pill px-3 py-2 f-s-11" :style="getAccountBadgeStyle(transaction.account_type)">
                        {{ formatAccountType(transaction.account_type) }}
                      </span>
                    </td>
                    <td class="py-3"><span class="f-s-13 text-muted">{{ transaction.account_number }}</span></td>
                    <td class="py-3">
                      <span class="badge rounded-pill px-3 py-2 f-s-11" 
                            :class="transaction.transaction_status === 'completed' ? 'bg-light text-success' : 'bg-light text-warning'">
                        <i :class="transaction.transaction_status === 'completed' ? 'ph-bold ph-check-circle' : 'ph-bold ph-clock'" class="me-1"></i>
                        {{ transaction.transaction_status }}
                      </span>
                    </td>
                    <td class="py-3"><span class="f-s-13 text-muted">{{ transaction.description }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredTransactions.length > 0" class="card-footer bg-white border-top-0 px-4 py-3" style="border-radius: 0 0 14px 14px;">
            <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <span class="text-muted f-s-13">
                Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredTransactions.length }} entries
              </span>
              <div class="d-flex align-items-center gap-3">
                <select class="form-select form-select-sm" v-model="perPage" @change="changePerPage" style="width: auto; border-radius: 8px;">
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
                <div v-if="totalPages > 1" class="d-flex align-items-center gap-1">
                  <button class="btn btn-sm border-0 rounded-2 fw-medium" :disabled="currentPage === 1" @click="changePage(currentPage - 1)"
                          style="color: #0891b2; background: transparent; min-width: 32px; height: 32px; padding: 0;">
                    <i class="ph-bold ph-caret-left"></i>
                  </button>
                  <template v-for="page in visiblePages" :key="page">
                    <button class="btn btn-sm border-0 rounded-2 fw-semibold f-s-12" @click="changePage(page)"
                            :style="page === currentPage ? 'background: linear-gradient(135deg, #0891b2, #06b6d4); color: white; min-width: 32px; height: 32px; padding: 0;' : 'color: #64748b; background: transparent; min-width: 32px; height: 32px; padding: 0;'">
                      {{ page }}
                    </button>
                  </template>
                  <button class="btn btn-sm border-0 rounded-2 fw-medium" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)"
                          style="color: #0891b2; background: transparent; min-width: 32px; height: 32px; padding: 0;">
                    <i class="ph-bold ph-caret-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STK Push Modal -->
    <div v-if="showStkModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="showStkModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0" style="border-radius: 16px; overflow: hidden;">
          <div class="modal-header border-0 pb-0 px-4 pt-4">
            <h5 class="fw-bold" style="color: #0f172a;">
              <i class="ph-duotone ph-money me-2 text-success"></i> M-Pesa Payment
            </h5>
            <button type="button" class="btn-close" @click="showStkModal = false"></button>
          </div>
          <div class="modal-body px-4 pb-4">
            <div v-if="errorMessage" class="alert border-0 mb-3" style="background: #fef2f2; color: #ef4444; border-radius: 12px;">
              <i class="ph-bold ph-warning-circle me-2"></i> {{ errorMessage }}
            </div>
            <div v-if="successMessage" class="alert border-0 mb-3" style="background: #ecfdf5; color: #10b981; border-radius: 12px;">
              <i class="ph-bold ph-check-circle me-2"></i> {{ successMessage }}
            </div>
            <form @submit.prevent="sendSTKPush">
              <div class="mb-3">
                <label class="form-label fw-medium f-s-13">Phone Number</label>
                <input type="tel" class="form-control" v-model="stkData.phone" required
                       placeholder="2547XXXXXXXX or 07XXXXXXXX" pattern="^(254|07|01)[0-9]{8,9}$" maxlength="12"
                       style="border-radius: 10px; padding: 10px 14px;">
                <small class="form-text text-muted f-s-12">e.g. 254712345678 or 0712345678</small>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium f-s-13">Amount (KES)</label>
                <input type="number" class="form-control" v-model.number="stkData.money_amount" required min="1" max="150000" step="1"
                       style="border-radius: 10px; padding: 10px 14px;">
                <small class="form-text text-muted f-s-12">Maximum: KES 150,000</small>
              </div>
              <div class="mb-4">
                <label class="form-label fw-medium f-s-13">Transaction Type</label>
                <select class="form-select" v-model="stkData.transaction_type" required style="border-radius: 10px; padding: 10px 14px;">
                  <option value="" disabled>Select type</option>
                  <option value="share_capital">Share Capital (SC)</option>
                  <option value="savings">Deposit Contribution (SV)</option>
                  <option value="land_installments">Plot Investments (LI)</option>
                </select>
              </div>
              <button type="submit" class="btn w-100 fw-semibold" :disabled="isSubmitting"
                      style="background: linear-gradient(135deg, #10b981, #34d399); color: white; border-radius: 10px; padding: 12px;">
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="ph-duotone ph-paper-plane-tilt me-2"></i>
                {{ isSubmitting ? 'Processing...' : 'Send Payment Request' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
    <!-- Statement Download Modal -->
    <div v-if="showStatementModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="showStatementModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0" style="border-radius: 16px; overflow: hidden;">
          <div style="height: 4px; background: linear-gradient(90deg, #10b981, #34d399, #6ee7b7);"></div>
          <div class="modal-header border-0 pb-0 px-4 pt-4">
            <h5 class="fw-bold" style="color: #0f172a;">
              <i class="ph-duotone ph-file-pdf me-2 text-success"></i> Download Statement
            </h5>
            <button type="button" class="btn-close" @click="showStatementModal = false"></button>
          </div>
          <div class="modal-body px-4 pb-4">
            <p class="text-muted f-s-13 mb-3">Select a period for your transaction statement.</p>

            <!-- Quick Duration Buttons -->
            <div class="d-flex flex-wrap gap-2 mb-4">
              <button v-for="preset in statementPresets" :key="preset.label"
                      class="btn btn-sm fw-semibold"
                      :style="statementDuration === preset.value ? 'background: linear-gradient(135deg, #10b981, #34d399); color: white; border-radius: 10px; padding: 6px 16px;' : 'background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; border-radius: 10px; padding: 6px 16px;'"
                      @click="applyStatementPreset(preset.value)">
                {{ preset.label }}
              </button>
            </div>

            <!-- Custom Date Range -->
            <div class="row g-3 mb-4">
              <div class="col-6">
                <label class="form-label fw-medium f-s-13 text-muted">Start Date</label>
                <input type="date" class="form-control" v-model="statementStartDate" @change="statementDuration = 'custom'"
                       style="border-radius: 10px; padding: 10px 14px;">
              </div>
              <div class="col-6">
                <label class="form-label fw-medium f-s-13 text-muted">End Date</label>
                <input type="date" class="form-control" v-model="statementEndDate" @change="statementDuration = 'custom'"
                       style="border-radius: 10px; padding: 10px 14px;">
              </div>
            </div>

            <!-- Selected Period Summary -->
            <div v-if="statementStartDate && statementEndDate" class="p-3 rounded-3 mb-4" style="background: #ecfdf5; border-left: 4px solid #10b981;">
              <p class="mb-0 f-s-13" style="color: #065f46;">
                <i class="ph-duotone ph-calendar me-1"></i>
                <strong>Period:</strong> {{ formatDate(statementStartDate) }} — {{ formatDate(statementEndDate) }}
              </p>
            </div>

            <div class="d-flex gap-2">
              <button class="btn fw-semibold flex-grow-1" @click="downloadPdf" :disabled="isDownloading || !statementStartDate || !statementEndDate"
                      style="background: linear-gradient(135deg, #10b981, #34d399); color: white; border-radius: 10px; padding: 12px;">
                <span v-if="isDownloading" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="ph-duotone ph-download-simple me-2"></i>
                {{ isDownloading ? 'Generating...' : 'Download PDF' }}
              </button>
              <button class="btn btn-outline-secondary fw-semibold" @click="showStatementModal = false" style="border-radius: 10px; padding: 12px 20px;">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import api from '@/services/axios.js';
import { useUserStore } from '@/store';

export default {
  name: 'TransactionsPage',
  components: { AppLayout },
  data() {
    return {
      transactions: [],
      stkData: {
        phone: '',
        money_amount: '',
        transaction_type: ''
      },
      filters: {
        startDate: '',
        endDate: '',
        transactionType: ''
      },
      currentPage: 1,
      perPage: 10,
      isSubmitting: false,
      isLoading: true,
      showStkModal: false,
      showStatementModal: false,
      isDownloading: false,
      statementDuration: '3months',
      statementStartDate: '',
      statementEndDate: '',
      statementPresets: [
        { label: 'Last Month', value: '1month' },
        { label: 'Last 3 Months', value: '3months' },
        { label: 'Last 6 Months', value: '6months' },
        { label: 'Last Year', value: '1year' },
        { label: 'All Time', value: 'all' },
      ],
      errorMessage: '',
      successMessage: '',
      lastRequestTime: 0
    };
  },
  computed: {
    store() {
      return useUserStore();
    },
    filteredTransactions() {
      let filtered = [...this.transactions];

      // Filter by date range
      if (this.filters.startDate) {
        filtered = filtered.filter(t => {
          const transactionDate = new Date(t.created_at);
          const startDate = new Date(this.filters.startDate);
          return transactionDate >= startDate;
        });
      }

      if (this.filters.endDate) {
        filtered = filtered.filter(t => {
          const transactionDate = new Date(t.created_at);
          const endDate = new Date(this.filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          return transactionDate <= endDate;
        });
      }

      // Filter by transaction type
      if (this.filters.transactionType) {
        filtered = filtered.filter(t => t.transaction_type === this.filters.transactionType);
      }

      return filtered;
    },
    totalPages() {
      return Math.ceil(this.filteredTransactions.length / this.perPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      const end = this.startIndex + this.perPage;
      return end > this.filteredTransactions.length ? this.filteredTransactions.length : end;
    },
    paginatedTransactions() {
      return this.filteredTransactions.slice(this.startIndex, this.endIndex);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  mounted() {
    this.fetchTransactions();
    this.applyStatementPreset('3months');
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    changePerPage() {
      this.currentPage = 1;
    },
    applyFilters() {
      this.currentPage = 1;
    },
    clearFilters() {
      this.filters = {
        startDate: '',
        endDate: '',
        transactionType: ''
      };
      this.currentPage = 1;
    },
    
    // Client-side rate limiting
    checkRateLimit() {
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      
      // Prevent requests within 3 seconds of each other
      if (timeSinceLastRequest < 3000) {
        return false;
      }
      
      this.lastRequestTime = now;
      return true;
    },

    // Validate phone number format
    validatePhoneNumber(phone) {
      const phoneRegex = /^(254|07|01)[0-9]{8,9}$/;
      return phoneRegex.test(phone.replace(/\s/g, ''));
    },

    // Sanitize input to prevent XSS
    sanitizeInput(input) {
      if (typeof input !== 'string') return input;
      return input.replace(/[<>]/g, '');
    },

    async sendSTKPush() {
      // Clear previous messages
      this.errorMessage = '';
      this.successMessage = '';

      try {
        // Client-side rate limiting
        if (!this.checkRateLimit()) {
          this.errorMessage = 'Please wait a moment before making another request.';
          return;
        }

        // Validate all fields are filled
        if (!this.stkData.phone || !this.stkData.money_amount || !this.stkData.transaction_type) {
          this.errorMessage = 'Please fill all required fields';
          return;
        }

        // Validate phone number format
        if (!this.validatePhoneNumber(this.stkData.phone)) {
          this.errorMessage = 'Invalid phone number format. Use 254712345678 or 0712345678';
          return;
        }

        // Validate amount
        if (this.stkData.money_amount < 1 || this.stkData.money_amount > 150000) {
          this.errorMessage = 'Amount must be between KES 1 and KES 150,000';
          return;
        }

        const store = useUserStore();
        const memberId = store.memberId;

        if (!memberId) {
          this.errorMessage = 'User session not found. Please log in again.';
          return;
        }

        // Sanitize inputs
        const sanitizedPhone = this.sanitizeInput(this.stkData.phone);
        const sanitizedTransactionType = this.sanitizeInput(this.stkData.transaction_type);

        this.isSubmitting = true;

        const response = await api.post(
          '/api/mpesa/stk/push',
          {
            phone: sanitizedPhone,
            money_amount: this.stkData.money_amount,
            transaction_type: sanitizedTransactionType,
            member_id: memberId,
          }
        );

        if (response.data.ResponseCode === '0') {
          this.successMessage = 'Payment request sent successfully! Please check your phone to complete the payment.';
          
          // Reset form
          this.stkData = { phone: '', money_amount: '', transaction_type: '' };
          
          // Close modal after 3 seconds
          setTimeout(() => {
            this.showStkModal = false;
            this.successMessage = '';
          }, 3000);

          // Refresh transactions after 10 seconds to allow for callback processing
          setTimeout(() => {
            this.fetchTransactions();
          }, 10000);

        } else {
          this.errorMessage = `M-Pesa error: ${response.data.message || 'Request not accepted'}`;
        }

      } catch (error) {
        console.error('STK Push Error:', error);
        
        if (error.response) {
          // Server responded with error
          if (error.response.status === 403) {
            this.errorMessage = 'Unauthorized. You can only make payments for your own account.';
          } else if (error.response.status === 429) {
            this.errorMessage = 'Too many requests. Please wait a moment before trying again.';
          } else {
            this.errorMessage = error.response.data?.message || 'Failed to send payment request. Please try again.';
          }
        } else if (error.request) {
          // No response received
          this.errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          // Other errors
          this.errorMessage = 'An unexpected error occurred. Please try again.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async downloadPdf() {
      try {
        const store = useUserStore();
        const memberId = store.memberId;

        if (!memberId) {
          alert('User session not found. Please log in again.');
          return;
        }

        this.isDownloading = true;

        let url = `/api/members/${memberId}/transactions/pdf`;
        const params = [];
        if (this.statementStartDate) params.push(`start_date=${this.statementStartDate}`);
        if (this.statementEndDate) params.push(`end_date=${this.statementEndDate}`);
        if (params.length) url += '?' + params.join('&');

        const response = await api.get(url, {
          responseType: 'blob',
          timeout: 30000
        });

        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        const dateLabel = this.statementStartDate && this.statementEndDate
          ? `${this.statementStartDate}_to_${this.statementEndDate}`
          : new Date().toISOString().split('T')[0];
        link.setAttribute('download', `transactions_statement_${dateLabel}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);

        this.showStatementModal = false;
      } catch (error) {
        console.error('PDF Download Error:', error);
        alert(error.response?.data?.message || 'Failed to download statement. Please try again.');
      } finally {
        this.isDownloading = false;
      }
    },
    applyStatementPreset(value) {
      this.statementDuration = value;
      const today = new Date();
      this.statementEndDate = today.toISOString().split('T')[0];

      if (value === 'all') {
        this.statementStartDate = '2015-01-01';
      } else if (value === '1month') {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        this.statementStartDate = d.toISOString().split('T')[0];
      } else if (value === '3months') {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 3);
        this.statementStartDate = d.toISOString().split('T')[0];
      } else if (value === '6months') {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 6);
        this.statementStartDate = d.toISOString().split('T')[0];
      } else if (value === '1year') {
        const d = new Date(today);
        d.setFullYear(d.getFullYear() - 1);
        this.statementStartDate = d.toISOString().split('T')[0];
      }
    },

    async fetchTransactions() {
      try {
        this.isLoading = true;
        const store = useUserStore();
        const memberId = store.memberId;

        if (!memberId) {
          throw new Error('User ID not found');
        }

        const response = await api.get(`/api/members/${memberId}/transactions`);
        
        this.transactions = response.data.transactions || [];

      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatAccountType(type) {
      const map = { share_capital: 'Share Capital', savings: 'Deposit Contribution', land_installments: 'Plot Investments' };
      return map[type] || type;
    },
    getAccountBadgeStyle(type) {
      const map = {
        share_capital: 'background: #ecfeff; color: #0891b2;',
        savings: 'background: #ecfdf5; color: #10b981;',
        land_installments: 'background: #fefce8; color: #f59e0b;',
      };
      return map[type] || 'background: #f1f5f9; color: #64748b;';
    },
  }
}
</script>